import { Stack } from '@startuptools/ui/Stack';
import { Trans } from '@lingui/react';
import { Button } from '@startuptools/ui/joy/Button';
import { StepProps } from '../../types';
import { Alert } from '@startuptools/ui/joy/Alert';
import { ToggleTextField } from '../ToggleTextField';
import { arbitrator, consent, majority, precedence, redemption } from '../../reserverations';
import * as React from 'react';
import { ConfirmDialog } from '@startuptools/ui/ConfirmDialog';

export const StepReservations = ({ form, onSave, loading }: StepProps) => {
  const [showDefaultTextDialog, setShowDefaultTextDialog] = React.useState(false);

  React.useEffect(() => {
    const {
      havePostTransferPurchaseRight,
      postTransferPurchaseRightPrimaryLang,
      haveRightOfFirstRefusal,
      rightOfFirstRefusalPrimaryLang,
      haveConsent,
      consentPrimaryLang,
      haveMajority,
      majorityPrimaryLang,
      haveArbitrator,
      arbitratorPrimaryLang,
    } = form.getValues();

    if (
      (havePostTransferPurchaseRight && !postTransferPurchaseRightPrimaryLang) ||
      (haveRightOfFirstRefusal && !rightOfFirstRefusalPrimaryLang) ||
      (haveConsent && !consentPrimaryLang) ||
      (haveMajority && !majorityPrimaryLang) ||
      (haveArbitrator && !arbitratorPrimaryLang)
    ) {
      setShowDefaultTextDialog(true);
    }
  }, [form]);

  return (
    <form
      onSubmit={form.handleSubmit(
        ({
          havePostTransferPurchaseRight,
          postTransferPurchaseRightPrimaryLang,
          changePostTransferPurchaseRight,
          haveRightOfFirstRefusal,
          rightOfFirstRefusalPrimaryLang,
          changeRightOfFirstRefusal,
          haveConsent,
          consentPrimaryLang,
          changeConsent,
          haveMajority,
          majorityPrimaryLang,
          changeMajority,
          haveArbitrator,
          arbitratorPrimaryLang,
          changeArbitrator,
        }) =>
          onSave({
            havePostTransferPurchaseRight,
            postTransferPurchaseRightPrimaryLang,
            changePostTransferPurchaseRight,
            haveRightOfFirstRefusal,
            rightOfFirstRefusalPrimaryLang,
            changeRightOfFirstRefusal,
            haveConsent,
            consentPrimaryLang,
            changeConsent,
            haveMajority,
            majorityPrimaryLang,
            changeMajority,
            haveArbitrator,
            arbitratorPrimaryLang,
            changeArbitrator,
          }),
      )}
    >
      <Stack gap={2}>
        {showDefaultTextDialog && (
          <ConfirmDialog
            header={<Trans id="Default text" />}
            confirmProps={{ children: <Trans id="Yes" /> }}
            onCancel={() => setShowDefaultTextDialog(false)}
            onConfirm={() => {
              if (
                form.getValues('havePostTransferPurchaseRight') &&
                !form.getValues('postTransferPurchaseRightPrimaryLang')
              ) {
                form.setValue('postTransferPurchaseRightPrimaryLang', redemption);
              }
              if (form.getValues('haveRightOfFirstRefusal') && !form.getValues('rightOfFirstRefusalPrimaryLang')) {
                form.setValue('rightOfFirstRefusalPrimaryLang', precedence);
              }
              if (form.getValues('haveConsent') && !form.getValues('consentPrimaryLang')) {
                form.setValue('consentPrimaryLang', consent);
              }
              if (form.getValues('haveMajority') && !form.getValues('majorityPrimaryLang')) {
                form.setValue('majorityPrimaryLang', majority);
              }
              if (form.getValues('haveArbitrator') && !form.getValues('arbitratorPrimaryLang')) {
                form.setValue('arbitratorPrimaryLang', arbitrator);
              }
              setShowDefaultTextDialog(false);
            }}
          >
            <Trans id="According to data from Bolagsverket, the company has one or more reservations set in the articles of association. Do you want to set Bolagsverket's default text for them?" />
          </ConfirmDialog>
        )}
        <Alert>
          <Trans id="Marking a value as changed will include a decision regarding the change in the connected meeting. " />
        </Alert>
        <ToggleTextField
          label={<Trans id="Post sale purchase right" />}
          booleanName="havePostTransferPurchaseRight"
          stringName="postTransferPurchaseRightPrimaryLang"
          key="postTransferPurchaseRightPrimaryLang"
          changeName="changePostTransferPurchaseRight"
          defaultValue={redemption}
          form={form}
        />
        <ToggleTextField
          label={<Trans id="Right of first refusal" />}
          booleanName="haveRightOfFirstRefusal"
          stringName="rightOfFirstRefusalPrimaryLang"
          key="rightOfFirstRefusalPrimaryLang"
          changeName="changeRightOfFirstRefusal"
          defaultValue={precedence}
          form={form}
        />
        <ToggleTextField
          label={<Trans id="Board approval required" />}
          booleanName="haveConsent"
          key="haveConsent"
          stringName="consentPrimaryLang"
          changeName="changeConsent"
          form={form}
          defaultValue={consent}
        />
        <ToggleTextField
          label={<Trans id="Voting rights requirement" />}
          booleanName="haveMajority"
          key="haveMajority"
          stringName="majorityPrimaryLang"
          changeName="changeMajority"
          form={form}
          defaultValue={majority}
        />
        <ToggleTextField
          label={<Trans id="Dispute resolution by arbitration" />}
          booleanName="haveArbitrator"
          key="haveArbitrator"
          stringName="arbitratorPrimaryLang"
          changeName="changeArbitrator"
          form={form}
          defaultValue={arbitrator}
        />
        <Stack direction="row" justifyContent="flex-start">
          <Button type="submit" loading={loading}>
            <Trans id="Next" />
          </Button>
        </Stack>
      </Stack>
    </form>
  );
};
